/*
  This file is created to prevent a bug that running the Sentry.init function happened after the
  import * as Sentry from '@sentry/browser'; statement in configure-store.* in app-store package.
 */
import { init as initializeSentry } from '@sentry/browser';

import { isDev } from '$app-web/utils';

const SENTRY_EXCLUSIONS: Record<string, Array<{ errorName: string; errorCode?: number; errorExtraInfoSubstring?: string }>> = {
    '^(/?api)?/?study/info/register/$': [
        { errorName: 'info_reg_already_reg' },
        { errorName: 'study_participation_ended', errorCode: 1035 },
        { errorName: 'info_reg_not_invited', errorCode: 1044 },
    ],
    '^(?!.*\\/v1\\/).*': [{ errorName: 'param_invalid', errorExtraInfoSubstring: 'matching query does not exist' }],
    '^(/?api)?/?study/activity/session/create/$': [{ errorName: 'param_invalid', errorExtraInfoSubstring: 'tl_id not found' }],
    '^(/?api)?/?v1/auth/reset_password/$': [
        { errorName: 'param_invalid', errorExtraInfoSubstring: 'There is not user registered with the specified email address' },
    ],
    '^(/?api)?/?v1/user/(email/)?$': [{ errorName: 'param_invalid', errorExtraInfoSubstring: 'Email already exists' }],
    '^(/?api)?/?v1/auth/set_password/$': [
        { errorName: 'password_used_before' },
        { errorName: 'param_invalid', errorExtraInfoSubstring: 'Invalid reset token' },
    ],
    '^(/?api)?/?v1/activity/session/time_user_def/': [
        { errorName: 'value_error', errorExtraInfoSubstring: 'Invalid scheduled_time' },
    ],
    '^(/?api)?/?v1/activity/session/time/': [
        { errorName: 'param_invalid', errorExtraInfoSubstring: 'Expiry time' },
        { errorName: 'param_invalid', errorExtraInfoSubstring: 'Cannot change a non-TIME TL session' },
    ],
    '^(/?api)?/?v1/activity/session/$': [{ errorName: 'uuid_parsing' }, { errorName: 'get_attribute_error' }],
    '^(/?api)?/?v1/survey/$': [
        { errorName: 'string_pattern_mismatch' },
        { errorName: 'value_error', errorExtraInfoSubstring: 'Criteria syntax error detected' },
    ],
    '^(/?api)?/?study/registration/invitation/create/$': [
        { errorName: 'invite_invalid_param', errorExtraInfoSubstring: 'Participation start time can not be in the past' },
    ],
    '^(/?api)?/?v1/data_source/(\\?study_id=\\d+)?$': [
        { errorName: 'param_invalid', errorExtraInfoSubstring: 'Invalid study_id' },
        { errorName: 'param_invalid', errorExtraInfoSubstring: 'Your study already have data source' },
    ],
    '^(/?api)?/?study/survey/image/upload/$': [{ errorName: 'contentTooLarge' }],
    '^(/?api)?/?study/survey/release/$': [{ errorName: 'invalid_survey_id', errorExtraInfoSubstring: 'Cannot release a survey' }],
    '^(/?api)?/?study/survey/save_changes/$': [{ errorName: 'param_invalid', errorExtraInfoSubstring: 'validation error' }],
    '^(/?api)?/?v1/participation/\\d+/$': [{ errorName: 'not_found', errorExtraInfoSubstring: 'Study does not exist' }],
    '^(/?api)?/?v1/activity/\\?study_id=\\d+$': [{ errorName: 'param_invalid', errorExtraInfoSubstring: 'Invalid study_id' }],
    '^(/?api)?/?v1/registration/reset_participation_by_participant/$': [
        { errorName: 'param_invalid', errorExtraInfoSubstring: 'Participant is not Active' },
    ],
};

function shouldExcludeFromSentry(
    url: string | undefined,
    errorName: unknown,
    errorCode?: unknown,
    errorExtraInfo?: unknown
): boolean {
    if (!url || typeof errorName !== 'string') {
        return false;
    }

    return Object.entries(SENTRY_EXCLUSIONS).some(
        ([urlPattern, exclusions]) =>
            new RegExp(urlPattern).test(url) &&
            exclusions.some(
                (exclusion) =>
                    (!exclusion.errorName || exclusion.errorName === errorName) &&
                    (!exclusion.errorCode || exclusion.errorCode === errorCode) &&
                    (!exclusion.errorExtraInfoSubstring ||
                        (typeof errorExtraInfo === 'string' &&
                            errorExtraInfo.toLowerCase().includes(exclusion.errorExtraInfoSubstring.toLowerCase())))
            )
    );
}

export function initSentry() {
    if (!isDev() && process.env.REACT_APP_RELEASE_FOR !== 'local') {
        // eslint-disable-next-line no-console
        console.log('Initialized Sentry');

        initializeSentry({
            dsn: 'https://c69b177d5a69b439d09ed8b581d012f5@sentry.avicennaresearch.com/15',
            tracesSampleRate: 1.0,
            release: `${process.env.REACT_APP_RELEASE_TAG}`,
            environment: process.env.REACT_APP_RELEASE_FOR,
            debug: true,
            attachStacktrace: true,
            normalizeDepth: 10,
            ignoreErrors: [
                // Benign browser notice raised when a ResizeObserver callback triggers another
                // layout in the same frame; the browser simply delivers it on the next frame.
                // Older browsers word it as "loop limit exceeded", current ones as below.
                'ResizeObserver loop limit exceeded',
                'ResizeObserver loop completed with undelivered notifications',
                // A promise rejected with a DOM Event or an empty object carries no app stack; these
                // come from browser extensions / injected scripts (observed alongside
                // webkit-masked-url frames), not from our code.
                'Non-Error promise rejection captured with keys: currentTarget, detail, isTrusted, target',
                'Non-Error promise rejection captured with keys: [object has no keys]',
            ],
            // Errors whose frames point at browser-extension code (Safari reports injected
            // extension scripts as webkit-masked-url), matching the new web app's config.
            denyUrls: [
                /extensions\//i,
                /^chrome-extension:\/\//i,
                /^moz-extension:\/\//i,
                /^safari-extension:\/\//i,
                /^webkit-masked-url:\/\//i,
            ],
            beforeBreadcrumb(breadcrumb, hint) {
                try {
                    if (breadcrumb?.category?.startsWith('ui')) {
                        return {
                            ...breadcrumb,
                            // To trace issues easier, we can't simply rely on CSS selectors Sentry uses by default since they're mostly generated automatically in our app.
                            message: `${breadcrumb.message}: ${hint?.event?.target?.innerText?.substring(0, 100) ?? ''}`,
                        };
                    }
                    // eslint-disable-next-line no-empty
                } catch (e) {}
                return breadcrumb;
            },
            async beforeSend(event, hint) {
                // We have different error mappers and "client" logics. That's why we need to extract the response information from multiple sources.
                const responseStatusFromExtra = (event.extra?.response as { status?: number })?.status;
                const originalException = hint?.originalException as
                    | {
                          status?: number;
                          code?: string | number;
                          innerError?: {
                              status?: number;
                              data?: {
                                  errorName?: string;
                                  errorCode?: number;
                                  errorExtraInfo?: string;
                                  detail?: Array<{ type?: string; msg?: string }>;
                              };
                              config?: { url?: string };
                          };
                      }
                    | undefined;
                const responseStatusFromOriginalException = originalException?.status ?? originalException?.innerError?.status;
                const responseStatus = responseStatusFromExtra ?? responseStatusFromOriginalException;

                const urlFromExtra = (event.extra?.request as { url?: string })?.url;
                const urlFromOriginalException = originalException?.innerError?.config?.url;
                const url = urlFromExtra ?? urlFromOriginalException ?? event.request?.url;

                const responseDataFromExtra = (
                    event.extra?.response as {
                        data?: { error_name?: string; error_code?: number; error_extra_info?: string };
                    }
                )?.data;
                const innerErrorData = originalException?.innerError?.data;
                const responseDataFromOriginalException = innerErrorData?.errorName
                    ? {
                          error_name: innerErrorData.errorName,
                          error_code: innerErrorData.errorCode,
                          error_extra_info: innerErrorData.errorExtraInfo,
                      }
                    : undefined;
                // Normalize Pydantic validation errors (FastAPI) which use detail[].type instead of error_name
                const pydanticDetail = innerErrorData?.detail?.[0];
                const responseDataFromPydantic = pydanticDetail?.type
                    ? { error_name: pydanticDetail.type, error_code: undefined, error_extra_info: pydanticDetail.msg }
                    : undefined;
                // Fallback: use exception's own code (e.g. 'contentTooLarge' from error-mapper) when response has no error data
                const exceptionCode = originalException?.code;
                const responseDataFromExceptionCode =
                    typeof exceptionCode === 'string'
                        ? { error_name: exceptionCode, error_code: undefined, error_extra_info: undefined }
                        : undefined;

                const responseData =
                    responseDataFromExtra ??
                    responseDataFromOriginalException ??
                    responseDataFromPydantic ??
                    responseDataFromExceptionCode;

                if (hint?.originalException) {
                    try {
                        // eslint-disable-next-line no-param-reassign
                        event.extra = {
                            ...event.extra,
                            originalException: hint.originalException,
                        };
                    } catch {
                        // Ignore serialization errors (circular references, etc.)
                        // Sentry's normalizeDepth will handle circular references during final serialization
                    }
                }

                // Exclude all 5xx and some 4xx HTTP response status codes
                if (
                    responseStatus != null &&
                    ((responseStatus >= 500 && responseStatus < 600) || [403, 404, 429].includes(responseStatus))
                ) {
                    return null;
                }

                if (responseStatus === 400 || responseStatus === 413) {
                    // TODO-MAYBE: Change shouldExcludeFromSentry to include response status in itself as well. Then move this and previous if to that function.
                    if (
                        shouldExcludeFromSentry(
                            url,
                            responseData?.error_name,
                            responseData?.error_code,
                            responseData?.error_extra_info
                        )
                    ) {
                        return null;
                    }
                }
                const shouldSkipEvent = (event?.exception?.values ?? []).some(
                    ({ type = '-', value = '-' }) =>
                        (type.includes('SyntaxError') && value.includes('<')) || type.includes('ChunkLoadError')
                );

                return shouldSkipEvent ? null : event;
            },
        });
    }
}
